import { useState, useCallback, useEffect } from "react";
import { supabase } from "@/lib/supabaseClient";

type Theme = {
  bg: string;
  surface: string;
  accent: string;
  accentText: string;
  text: string;
  textMuted: string;
  textFaint: string;
  border: string;
  inputBg: string;
  font: string;
  ornament: string;
};

type InvitationData = {
  id: string;
  tenant_id: string;
  rsvp_message: string | null;
};

type AttendanceStatus = "yes" | "no" | "maybe";

interface RsvpModalProps {
  invitation: InvitationData;
  theme: Theme;
  onClose: () => void;
  onSubmitted: () => void;
}

export default function RsvpModal({ invitation, theme, onClose, onSubmitted }: RsvpModalProps) {
  const [visible, setVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [attendance, setAttendance] = useState<AttendanceStatus>("yes");
  const [guestName, setGuestName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [guestCount, setGuestCount] = useState(1);
  const [note, setNote] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 20);
    return () => clearTimeout(t);
  }, []);

  const handleClose = useCallback(() => {
    setVisible(false);
    setTimeout(onClose, 300);
  }, [onClose]);

  const validate = useCallback(() => {
    const errs: Record<string, string> = {};
    if (!guestName.trim()) errs.guestName = "Tu nombre es requerido.";
    if (!phone.trim() && !email.trim()) errs.contact = "Ingresa al menos teléfono o email.";
    return errs;
  }, [guestName, phone, email]);

  const handleSubmit = useCallback(async () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setErrors({});
    if (submitting) return;
    setSubmitting(true);

    const { error } = await supabase.from("invitation_rsvps").insert({
      invitation_id:     invitation.id,
      tenant_id:         invitation.tenant_id,
      guest_name:        guestName.trim(),
      phone:             phone.trim() || null,
      email:             email.trim() || null,
      attendance_status: attendance,
      guest_count:       attendance === "yes" ? guestCount : 1,
      message:           note.trim() || null,
    });

    setSubmitting(false);

    if (error) {
      setErrors({ submit: error.message });
      return;
    }

    setVisible(false);
    setTimeout(onSubmitted, 300);
  }, [validate, submitting, invitation, guestName, phone, email, attendance, guestCount, note, onSubmitted]);

  const inputStyle: React.CSSProperties = {
    backgroundColor: theme.inputBg,
    borderColor: theme.border,
    color: theme.text,
    outline: "none",
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center"
      onClick={handleClose}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 transition-opacity duration-300"
        style={{ opacity: visible ? 1 : 0 }}
      />

      {/* Sheet */}
      <div
        className="relative w-full max-w-md mx-auto rounded-t-3xl overflow-hidden transition-transform duration-300"
        style={{
          backgroundColor: theme.bg,
          transform: visible ? "translateY(0)" : "translateY(100%)",
          maxHeight: "90dvh",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full" style={{ backgroundColor: theme.border }} />
        </div>

        {/* Scrollable content */}
        <div className="overflow-y-auto px-6 pb-8 pt-2" style={{ maxHeight: "calc(90dvh - 32px)" }}>
          {/* Header */}
          <div className="flex items-center justify-between mb-5">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] font-semibold mb-0.5" style={{ color: theme.accent + "90" }}>
                RSVP
              </p>
              <h3 className="text-lg font-bold" style={{ color: theme.text, fontFamily: theme.font }}>
                ¿Confirmas tu asistencia?
              </h3>
            </div>
            <button
              onClick={handleClose}
              className="w-8 h-8 flex items-center justify-center rounded-full cursor-pointer"
              style={{ backgroundColor: theme.border + "80", color: theme.textMuted }}
            >
              <i className="ri-close-line" />
            </button>
          </div>

          {invitation.rsvp_message && (
            <p className="text-sm leading-relaxed mb-4 text-center italic" style={{ color: theme.textMuted, fontFamily: theme.font }}>
              &ldquo;{invitation.rsvp_message}&rdquo;
            </p>
          )}

          <div className="space-y-4">
            {/* Attendance */}
            <div className="grid grid-cols-3 gap-2">
              {(["yes", "no", "maybe"] as AttendanceStatus[]).map((opt) => {
                const labels = { yes: "Asistiré", no: "No podré", maybe: "Tal vez" };
                const icons  = { yes: "ri-checkbox-circle-line", no: "ri-close-circle-line", maybe: "ri-question-line" };
                const active = attendance === opt;
                return (
                  <button
                    key={opt}
                    onClick={() => setAttendance(opt)}
                    className="flex flex-col items-center gap-1.5 py-3 px-2 rounded-2xl border text-xs font-semibold transition-all cursor-pointer"
                    style={{
                      borderColor:     active ? theme.accent : theme.border,
                      backgroundColor: active ? theme.accent + "18" : "transparent",
                      color:           active ? theme.accent : theme.textFaint,
                    }}
                  >
                    <span className="w-5 h-5 flex items-center justify-center">
                      <i className={`${icons[opt]} text-base`} />
                    </span>
                    {labels[opt]}
                  </button>
                );
              })}
            </div>

            {/* Name */}
            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: theme.textMuted }}>
                Nombre completo <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={guestName}
                onChange={(e) => { setGuestName(e.target.value); setErrors((p) => ({ ...p, guestName: "" })); }}
                placeholder="Tu nombre"
                className="w-full rounded-xl px-4 py-3 text-sm border transition-colors"
                style={errors.guestName ? { ...inputStyle, borderColor: "#ef4444" } : inputStyle}
              />
              {errors.guestName && <p className="text-red-400 text-xs mt-1">{errors.guestName}</p>}
            </div>

            {/* Phone + Email */}
            <div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: theme.textMuted }}>Teléfono</label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => { setPhone(e.target.value); setErrors((p) => ({ ...p, contact: "" })); }}
                    placeholder="+1 555..."
                    className="w-full rounded-xl px-4 py-3 text-sm border"
                    style={errors.contact ? { ...inputStyle, borderColor: "#ef4444" } : inputStyle}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: theme.textMuted }}>Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setErrors((p) => ({ ...p, contact: "" })); }}
                    placeholder="tu@email.com"
                    className="w-full rounded-xl px-4 py-3 text-sm border"
                    style={errors.contact ? { ...inputStyle, borderColor: "#ef4444" } : inputStyle}
                  />
                </div>
              </div>
              {errors.contact && <p className="text-red-400 text-xs mt-1.5">{errors.contact}</p>}
              <p className="text-xs mt-1" style={{ color: theme.textFaint }}>Al menos uno es requerido.</p>
            </div>

            {/* Guest count */}
            {attendance === "yes" && (
              <div>
                <label className="block text-xs font-medium mb-2" style={{ color: theme.textMuted }}>
                  Número de personas (incluido tú)
                </label>
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => setGuestCount((n) => Math.max(1, n - 1))}
                    className="w-10 h-10 rounded-full border flex items-center justify-center cursor-pointer"
                    style={{ borderColor: theme.border, color: theme.textMuted }}
                  >
                    <i className="ri-subtract-line" />
                  </button>
                  <span className="text-2xl font-bold w-8 text-center" style={{ color: theme.text, fontFamily: theme.font }}>
                    {guestCount}
                  </span>
                  <button
                    onClick={() => setGuestCount((n) => Math.min(20, n + 1))}
                    className="w-10 h-10 rounded-full border flex items-center justify-center cursor-pointer"
                    style={{ borderColor: theme.border, color: theme.textMuted }}
                  >
                    <i className="ri-add-line" />
                  </button>
                </div>
              </div>
            )}

            {/* Note */}
            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: theme.textMuted }}>
                Nota <span style={{ color: theme.textFaint }}>(opcional)</span>
              </label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Deja un mensaje para los anfitriones…"
                rows={3}
                maxLength={500}
                className="w-full rounded-xl px-4 py-3 text-sm border resize-none"
                style={inputStyle}
              />
              <p className="text-right text-xs mt-1" style={{ color: theme.textFaint }}>{note.length}/500</p>
            </div>

            {errors.submit && (
              <div className="rounded-xl px-4 py-3 bg-red-50 border border-red-200">
                <p className="text-red-600 text-xs">{errors.submit}</p>
              </div>
            )}

            {/* Submit */}
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="w-full py-4 rounded-2xl font-bold text-sm transition-all cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2 whitespace-nowrap"
              style={{ backgroundColor: theme.accent, color: theme.accentText }}
            >
              {submitting
                ? <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                : <><i className="ri-send-plane-fill" />Confirmar asistencia</>
              }
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}